import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { ShieldOff, UserX, Lock, CloudOff, MailCheck } from 'lucide-react';
// Locale LEGERE (React seul, zero i18next) — @gs/ui n'appelle JAMAIS t()
// (UI_STANDARDS §6). Sans GsLocaleProvider, useLocale() = null => defauts FR.
import { useLocale } from '@gs/i18n/localeContext';

/**
 * Les trois refus du Systeme C, tels que les portes SERVEUR les prononcent,
 * plus l'etat « acces non verifiable » quand /api/me n'a pas pu trancher.
 * Un kind = une CAUSE, pas une mise en page : c'est la cause qui dit a
 * l'utilisateur quoi faire ensuite.
 *
 * - `notProvisioned`   : le compte n'existe pas dans ug.Users (provisionOnMiss:false)
 *                        -> le serveur a repondu 403 <BC>_USER_NOT_PROVISIONED.
 * - `moduleNotEntitled`: etage 1 (ADR-067) — le module n'est pas actif pour la
 *                        compagnie (ug.CompanyModules). Aucun bypass corporate.
 * - `noRole`           : etage 2 — aucun role sur le bc_key (ug.UserCompanyBcRoles).
 * - `unverifiable`     : l'appel /api/me a echoue sans refus propre (500, reseau,
 *                        autre 403, 401). Ce n'est PAS un refus — l'ecran informe
 *                        et propose de reessayer. Ni ouverture silencieuse, ni
 *                        faux « non provisionne ».
 * - `accessPending`    : ADR-076 D3 — l'identite EXISTE et elle est legitime, mais
 *                        aucun role ne lui est encore attribue. CE N'EST PAS UN
 *                        REFUS : c'est l'etat NORMAL entre l'arrivee d'un collegue
 *                        et le moment ou son administrateur lui ouvre ses modules.
 *                        Teinte accueil (bleu), aucune tuile derriere, et la suite
 *                        est ecrite noir sur blanc. Le rouge des trois refus
 *                        ferait croire a une panne et rapporterait l'appel
 *                        telephonique qu'on veut supprimer.
 */
export type AccessGateKind =
  | 'notProvisioned'
  | 'moduleNotEntitled'
  | 'noRole'
  | 'unverifiable'
  | 'accessPending';

export interface AccessGateLabels {
  /** Titre. Defaut : copie interne selon `kind` + locale. */
  title?: string;
  /** Explication. Defaut : copie interne selon `kind` + locale. */
  description?: string;
  /** Ligne « connecte en tant que » DEJA interpolee. Defaut : construite depuis `email`. */
  signedInAs?: string;
  /** Libelle du CTA deconnexion. Defaut : « Se deconnecter » / « Sign out ». */
  logout?: string;
  /** Libelle du CTA reessayer (kind `unverifiable`). Defaut : « Reessayer » / « Try again ». */
  retry?: string;
}

export interface AccessGateProps {
  kind: AccessGateKind;
  onLogout: () => void;
  /** Affiche la ligne « connecte en tant que » (omis = pas de ligne). */
  email?: string;
  /** Textes ; chaque champ absent retombe sur le defaut du kind. */
  labels?: AccessGateLabels;
  /** Icone du container teinte (defaut : celle du kind). */
  icon?: LucideIcon;
  /**
   * Rejouer GET /api/me. Affiche le CTA seulement pour `unverifiable`.
   * L'appelant DOIT passer un refetch manuel (useMe a retry:false — pas de boucle).
   */
  onRetry?: () => void;
  /** Desactive le CTA reessayer pendant le refetch. */
  retrying?: boolean;
}

const KIND_ICON: Record<AccessGateKind, LucideIcon> = {
  notProvisioned: UserX,
  moduleNotEntitled: Lock,
  noRole: ShieldOff,
  unverifiable: CloudOff,
  accessPending: MailCheck,
};

/** Les kinds qui ne sont PAS un refus : ni icone rouge, ni ton d'echec. */
const NON_REFUS: ReadonlySet<AccessGateKind> = new Set<AccessGateKind>([
  'unverifiable',
  'accessPending',
]);

const LOGOUT_BTN =
  'inline-flex touch-manipulation items-center gap-2 rounded-lg bg-slate-800 px-5 py-2 text-field font-semibold text-white transition-colors hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500';

const RETRY_BTN =
  'inline-flex touch-manipulation items-center gap-2 rounded-lg bg-slate-800 px-5 py-2 text-field font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-600 dark:hover:bg-slate-500';

const LOGOUT_SECONDARY_BTN =
  'inline-flex touch-manipulation items-center gap-2 rounded-lg border border-slate-200 bg-transparent px-5 py-2 text-field font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700';

// Defauts FR/EN internes : @gs/ui ne peut pas appeler t(), et une app qui n'a pas
// encore de catalogue doit quand meme afficher un refus lisible. Une app i18n
// passe ses propres `labels` et ces defauts ne servent jamais.
const COPY: Record<'fr' | 'en', Record<AccessGateKind, { title: string; description: string }> & {
  logout: string;
  retry: string;
  signedInAs: (email: string) => string;
}> = {
  fr: {
    notProvisioned: {
      title: 'Accès non provisionné',
      description: "Votre compte n'est pas encore enregistré pour cette application. Demandez l'accès à votre administrateur.",
    },
    moduleNotEntitled: {
      title: 'Module non activé',
      description: "Ce module n'est pas activé pour votre compagnie. Demandez l'accès à votre administrateur.",
    },
    noRole: {
      title: 'Accès refusé',
      description: "Aucun rôle ne vous est attribué dans cette application. Demandez l'accès à votre administrateur.",
    },
    unverifiable: {
      title: 'Accès non vérifiable',
      description: "Nous n'avons pas pu vérifier votre accès pour le moment. Réessayez. Ce n'est pas un refus : le service n'a pas répondu.",
    },
    accessPending: {
      title: 'Bienvenue chez Groupe Signalisation',
      description: "Votre compte est reconnu. Vos accès seront activés par votre gestionnaire — vos applications apparaîtront ici dès que ce sera fait. Pour accélérer les choses, écrivez à l'administrateur de votre compagnie en lui donnant l'adresse ci-dessous.",
    },
    logout: 'Se déconnecter',
    retry: 'Réessayer',
    signedInAs: (email: string) => `Connecté en tant que ${email}`,
  },
  en: {
    notProvisioned: {
      title: 'Access not provisioned',
      description: 'Your account is not registered for this application yet. Ask your administrator for access.',
    },
    moduleNotEntitled: {
      title: 'Module not enabled',
      description: 'This module is not enabled for your company. Ask your administrator for access.',
    },
    noRole: {
      title: 'Access denied',
      description: 'You have no role in this application. Ask your administrator for access.',
    },
    unverifiable: {
      title: 'Access could not be verified',
      description: 'We could not verify your access just now. Try again. This is not a denial: the service did not respond.',
    },
    accessPending: {
      title: 'Welcome to Groupe Signalisation',
      description: 'Your account is recognized. Your manager will activate your access — your applications will show up here as soon as that is done. To speed things up, email your company administrator and give them the address below.',
    },
    logout: 'Sign out',
    retry: 'Try again',
    signedInAs: (email: string) => `Signed in as ${email}`,
  },
};

/**
 * Ecran de porte client Systeme C — une seule porte pour les trois refus
 * serveur et l'etat « acces non verifiable ». Absorbe `GsForbiddenScreen`,
 * qui n'est plus qu'une facade sur le kind `noRole` : un seul chemin de
 * rendu, plus deux ecrans a faire diverger.
 *
 * GARDE CLIENT = CONFORT D'AFFICHAGE, JAMAIS UNE GARDE. L'autorite est SERVEUR
 * (requireBc / requireRole / entitlement), sans exception : cet ecran evite
 * seulement d'afficher un shell mort le temps que le serveur reponde. Un refus
 * affiche sans refus serveur derriere serait un mode de panne, pas une securite
 * (precedent gest GA-02 fail-closed).
 *
 * `unverifiable` n'est PAS un refus : teinte amber (informe), CTA Reessayer,
 * deconnexion en secondaire. `accessPending` non plus : teinte bleue (accueil),
 * un seul CTA. Les trois refus gardent red-500 + un seul CTA.
 *
 * Discipline DS : canvas pleine page `bg-page`, CTA slate, refus = red-500
 * semantique (icone) — JAMAIS la couleur de marque. L'accueil emprunte le bleu
 * informatif du systeme (`GS_SEMANTIC_COLORS.statusInfo`), pas l'orange de
 * marque : un ecran d'etat ne se peint pas aux couleurs de l'enseigne.
 */
export function AccessGate({ kind, onLogout, email, labels, icon, onRetry, retrying }: AccessGateProps) {
  const localeCtx = useLocale();
  const copy = COPY[localeCtx?.locale === 'en' ? 'en' : 'fr'];
  const Icon = icon ?? KIND_ICON[kind];
  const isUnverifiable = kind === 'unverifiable';
  const isWelcome = kind === 'accessPending';
  const isRefus = !NON_REFUS.has(kind);

  const title = labels?.title ?? copy[kind].title;
  const description = labels?.description ?? copy[kind].description;
  const signedInAs = labels?.signedInAs ?? (email ? copy.signedInAs(email) : undefined);
  const logoutLabel = labels?.logout ?? copy.logout;
  const retryLabel = labels?.retry ?? copy.retry;

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-page dark:bg-slate-900"
      data-access-gate={kind}
    >
      <div className="max-w-sm px-6 text-center">
        <div
          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${
            isWelcome
              ? 'bg-blue-50 dark:bg-blue-950/30'
              : isUnverifiable
                ? 'bg-amber-50 dark:bg-amber-950/30'
                : 'bg-red-50 dark:bg-red-950/30'
          }`}
        >
          <Icon
            size={28}
            className={isWelcome ? 'text-blue-600' : isRefus ? 'text-red-500' : 'text-amber-500'}
            aria-hidden
          />
        </div>
        <h1 className="mb-2 text-head font-semibold text-slate-800 dark:text-slate-100">{title}</h1>
        {description && (
          <p className={`${signedInAs ? 'mb-2' : 'mb-6'} text-field text-slate-500 dark:text-slate-400`}>
            {description}
          </p>
        )}
        {signedInAs && (
          <p className="mb-6 text-meta text-slate-400 dark:text-slate-500">{signedInAs}</p>
        )}
        {isUnverifiable && onRetry ? (
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              className={RETRY_BTN}
            >
              {retryLabel}
            </button>
            <button
              type="button"
              onClick={onLogout}
              className={LOGOUT_SECONDARY_BTN}
            >
              {logoutLabel}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onLogout}
            className={LOGOUT_BTN}
          >
            {logoutLabel}
          </button>
        )}
      </div>
    </div>
  );
}
